import * as React from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface TagEditorProps {
  id?: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Accessible name of the tag list. */
  label?: string;
}

/** Chips with remove buttons plus an input that adds on Enter or "Add". Duplicates are ignored. */
export function TagEditor({
  id,
  value,
  onChange,
  disabled = false,
  placeholder = 'Add a term…',
  label = 'Terms',
}: TagEditorProps) {
  const [draft, setDraft] = React.useState('');

  const add = () => {
    const term = draft.trim();
    if (!term) return;
    if (!value.some((v) => v.toLowerCase() === term.toLowerCase())) onChange([...value, term]);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {value.map((term) => (
            <li key={term}>
              <Badge variant="secondary" className="gap-1 pr-1 font-normal">
                {term}
                {disabled ? null : (
                  <button
                    type="button"
                    className="rounded-sm p-0.5 hover:bg-background/60"
                    onClick={() => onChange(value.filter((v) => v !== term))}
                    aria-label={`Remove ${term}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">None.</p>
      )}
      {disabled ? null : (
        <div className="flex gap-2">
          <Input
            id={id}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={placeholder}
            className="max-w-xs"
          />
          <Button type="button" variant="outline" onClick={add} disabled={!draft.trim()}>
            Add
          </Button>
        </div>
      )}
    </div>
  );
}
